import React from "react";
import { logDev } from "@/lib/logger";

// ---------------------------------------------------------------------------
// Minimal error boundary — catches any render-time exception thrown inside
// its children and shows a controlled fallback instead of letting the
// exception unmount the whole React tree (white-screen).
//
// Usage:
//   <ErrorBoundary label="conversion-panel" locale={locale}>
//     <ConversionPanel ... />
//   </ErrorBoundary>
// ---------------------------------------------------------------------------
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    logDev(`errorBoundary:${this.props.label || "unknown"}`, { error, info });
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    const locale = this.props.locale || "tr";
    const message = this.props.message ||
      (locale === "en"
        ? "This part of the page could not be displayed. Please try again."
        : "Bu bölüm şu anda görüntülenemedi. Lütfen tekrar deneyin.");
    return (
      <div
        data-testid={`error-boundary-${this.props.label || "unknown"}`}
        className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-5 text-sm"
      >
        <div className="font-semibold mb-1">
          {locale === "en" ? "Something went wrong here" : "Bir sorun oluştu"}
        </div>
        <div className="text-muted-foreground text-xs leading-relaxed">{message}</div>
        <button
          type="button"
          onClick={this.reset}
          className="mt-3 text-xs font-semibold text-primary hover:underline"
          data-testid={`error-boundary-retry-${this.props.label || "unknown"}`}
        >
          {locale === "en" ? "Try again" : "Tekrar dene"}
        </button>
      </div>
    );
  }
}
